import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../../context/ProjectContext';
import '../../styles/common.css';
import { useSearchParams, useNavigate, useLocation } from 'react-router';
import '../../styles/test-suites-overview.css';
import CreateTestSuiteModal from './CreateTestSuiteModal';
import EditTestSuiteModal from './EditTestSuiteModal';
import axios from 'axios';
import axiosInstance from '../../api/axiosInstance';
import { Pagination } from '../ui';
import LinkTestCasesModal from './LinkTestCasesModal';
import ExecutionHistoryModal from '../common/ExecutionHistoryModal';
import { ProjectStatusBadge, ProjectTypeBadge } from '../common';
import { formatDate as formatUserDate } from '../../utils/dateTimeFormat';

const TestSuitesOverview = ({ onViewProjectTestSuites }) => {
  const navigate = useNavigate();
  const { selectedProject: contextProject } = useProject();

  const [projects, setProjects] = useState([]);
  const [projectListForFilter, setProjectList] = useState([])
  const [cardValues, setCardValue] = useState({})
  const [testSuitList, setTestSuitList] = useState([])
  const [loading, setLoading] = useState(true);
  const [projectTestSuites, setProjectTestSuites] = useState({});
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputDisabled, setSearchInputDisabled] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterType, setFilterType] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [actionFeedback, setActionFeedback] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTestSuite, setSelectedTestSuite] = useState(null);
  const [testSuiteToDelete, setTestSuiteToDelete] = useState(null);
  const [isDeletingTestSuite, setIsDeletingTestSuite] = useState(false);
  const [testCount, setTestCount] = useState(0);
  const [testCasesCount, setTestCasesCount] = useState(0);
  const [testSuitForSelectedProj, setTestSuitForSelectedProj] = useState([])
  const [projectTestCasesCounts, setProjectTestCasesCounts] = useState({});
  const [projectTestSuiteTotalPages, setProjectTestSuiteTotalPages] = useState({});
  const [projectSuitesLoading, setProjectSuitesLoading] = useState({});
  const testSuitesPerPage = 10;
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSuiteId = searchParams.get('selectedSuite');

  // Route-based pagination for projects list
  const getPageFromSearchParams = (searchParams) => {
    const page = parseInt(searchParams.get('page'), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  };
  const currentPage = getPageFromSearchParams(searchParams) || 1;

  const setCurrentPage = useCallback((page) => {
    const validPage = Math.max(1, Number(page) || 1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (validPage <= 1) {
          next.delete('page');
        } else {
          next.set('page', String(validPage));
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // Route-based pagination for test suites within a project
  const getSuitePageFromSearchParams = (searchParams) => {
    const page = parseInt(searchParams.get('suite_page'), 10);
    return Number.isFinite(page) && page > 0 ? page : 1;
  };
  const suitePageFromUrl = getSuitePageFromSearchParams(searchParams);
  const suiteProjectFromUrl = searchParams.get('suite_project');

  const setSuitePageInUrl = useCallback((projectId, page) => {
    const validPage = Math.max(1, Number(page) || 1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('suite_project', String(projectId));
        if (validPage <= 1) {
          next.delete('suite_page');
        } else {
          next.set('suite_page', String(validPage));
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const getSuitePageForProject = useCallback((projectId) => {
    if (suiteProjectFromUrl && String(suiteProjectFromUrl) === String(projectId)) {
      return suitePageFromUrl;
    }
    return 1;
  }, [suiteProjectFromUrl, suitePageFromUrl]);
  const [originalProjects, setOriginalProjects] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [projectStatusFilter, setProjectStatusFilter] = useState('all');
  const location = useLocation(); // Using window.location to avoid issues with useLocation in modals
  const showActionFeedback = (message, type = 'success') => {
    setActionFeedback({ message, type });
    setTimeout(() => {
      setActionFeedback(null);
    }, 3000);
  };
  const [isLinkCasesModalOpen, setIsLinkCasesModalOpen] = useState(false);
  const [isExecutionHistoryModalOpen, setIsExecutionHistoryModalOpen] = useState(false);
  const [executionHistorySuite, setExecutionHistorySuite] = useState(null);

const [linkCasesMode, setLinkCasesMode] = useState('link');


  useEffect(() => {
    if (selectedSuiteId) {

      console.log('Focusing on test suite:', selectedSuiteId);

      const element = document.getElementById(`test-suite-${selectedSuiteId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        element.classList.add('highlighted');

        setTimeout(() => {
          element.classList.remove('highlighted');
        }, 3000);
      }
    }
  }, [selectedSuiteId]);

  // Fetch projects and their test suites
  const paginate = (items, pageNumber, pageSize) => {
    const startIndex = (pageNumber - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  };
  const handleViewAllProjects = () => {
    setFilterType('all');
    setExpandedProjects({});
  };

  const createTestSuit = (isOpen, project) => {

    setIsCreateModalOpen(isOpen)
    setTestSuitForSelectedProj(project)

  }
  const getTestSuitForProjects = async (projectId, page = 1) => {
    try {
      setProjectSuitesLoading((prev) => ({ ...prev, [projectId]: true }));
      const projectsRes = await axiosInstance.get(`/test_suite/list/${projectId}`, {
        params: {
          page: page || 1,
          page_size: testSuitesPerPage,
          sort_by: 1,
          sort_order: 2,
        },
      });
      setTestSuitList(projectsRes.test_suites);
      setProjectTestSuites((prev) => ({
        ...prev,
        [projectId]: projectsRes.test_suites,
      }));
      setProjectTestSuiteTotalPages((prev) => ({
        ...prev,
        [projectId]: projectsRes.total_pages || 1,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setProjectSuitesLoading((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const handleTestSuitePageChange = (projectId, page) => {
    setSuitePageInUrl(projectId, page);
  };

  const closeEidtTestForm = (project) => {
    const page = getSuitePageForProject(project.project_id);
    getTestSuitForProjects(project.project_id, page);
  }
  const fetchData = async (page, projects, isSearch = false, searchParam = null) => {
    if (isSearch) {
      setTableLoading(true);
    } else {
      setLoading(true);
    } try {
    const params = {
      page: page || 1,
      page_size: 10,
      search:  searchParam || searchQuery ||  "",
    };
    
  
    if (projectStatusFilter && projectStatusFilter !== 'all') {
      params.pro_status = projectStatusFilter;
    }

    params.workspace_id = parseInt(location.pathname.split('/')[1].split('_')[1], 10) || null;

    const projectsRes = await axiosInstance.get(
      '/projects/suite_dashboard_data',
      { params }
    );

      if (projects === 'all' || !projects) {
        setProjectList(projectsRes?.projects ?? []);
        setFilterType('');
      } else {
        setFilterType(projects);
      }

      const rawProjects = projectsRes?.projects ?? [];
      setCardValue({
        total_project_count: projectsRes?.total_project_count,
        page: projectsRes?.page,
        page_size: projectsRes?.page_size,
        total_pages: projectsRes?.total_pages,
        total_suites_all_projects: projectsRes?.total_suites_all_projects,
        total_test_cases_all_projects: projectsRes?.total_test_cases_all_projects,
        avg_pass_rate_all_projects: projectsRes?.avg_pass_rate_all_projects,
        total_cases_passed_all_projects: projectsRes?.total_cases_passed_all_projects,
        total_cases_failed_all_projects: projectsRes?.total_cases_failed_all_projects,
        total_cases_pending_all_projects: projectsRes?.total_cases_pending_all_projects,
        status_code: 200,
      });

      setProjects(rawProjects);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects. Please try again later.");
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };
  // Sync with global project context from NavBar
  useEffect(() => {
    if (contextProject?.id) {
      setSearchQuery(contextProject.name);
      setSearchInputDisabled(true);
      setExpandedProjects(prev => ({ ...prev, [contextProject.id]: true }));
      if (suiteProjectFromUrl !== String(contextProject.id)) {
        setSuitePageInUrl(contextProject.id, 1);
      }
    } else {
      setSearchInputDisabled(false);
      setSearchQuery("");
    }
  }, [contextProject]);

  // Fetch test suites when suite pagination URL or expanded project changes
  useEffect(() => {
    const projectId = suiteProjectFromUrl;
    if (!projectId) return;

    const isExpanded =
      expandedProjects[projectId] ||
      expandedProjects[parseInt(projectId, 10)];
    const isContextProject =
      contextProject?.id &&
      String(contextProject.id) === String(projectId);

    if (!isExpanded && !isContextProject) return;

    getTestSuitForProjects(projectId, suitePageFromUrl);
  }, [suitePageFromUrl, suiteProjectFromUrl, expandedProjects, contextProject?.id]);


  // useEffect(()=>{
  //   if(searchQuery == "" && !contextProject.id){
  //     fetchData(1, contextProject.id, true, contextProject.id);
  //   }
  // },[searchQuery,contextProject])

  // Fetch data when currentPage changes (route-based pagination)
  // This handles pagination for the current project or all projects
  useEffect(() => {
    if (contextProject?.id) {
      fetchData(currentPage, contextProject.id, true, contextProject.id);
      return;
    }

    const debounceTimer = setTimeout(() => {
      fetchData(currentPage, null, true, searchQuery);
    }, searchQuery ? 500 : 0);

    return () => clearTimeout(debounceTimer);
  }, [contextProject, currentPage, projectStatusFilter, searchQuery]);

  const formatDate = (dateString) => formatUserDate(dateString);

  const toggleProjectExpansion = (projectId, event) => {
    if (!expandedProjects[projectId]) {
      if (suiteProjectFromUrl !== String(projectId)) {
        setSuitePageInUrl(projectId, 1);
      }
    }
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));

  };
  const closeCreationModal = (project) => {
    const projectId = project?.project_id || contextProject?.id;
    if (projectId) {
      const page = getSuitePageForProject(projectId);
      getTestSuitForProjects(projectId, page);
    }
    setIsCreateModalOpen(false);
  };
  // const handleViewProjectTestSuites = (projectId) => {
  //   navigate(`/dashboard/test-suites/details?project_id=${projectId}`);
  // };
  const handleViewProjectTestSuites = (projectId, suiteId = null) => {
    if (!projectId) {
      console.error('Project ID is required');
      return;
    }
    
    let url = `../test-suites/details?project_id=${projectId}`;
    if (suiteId) {
      url += `&suite_id=${suiteId}`;
    }
    
    navigate(url);
  };
  const handleSearch = useCallback((e) => {
    if (searchInputDisabled) return;
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  }, [searchInputDisabled, setCurrentPage]);

  const handleProjectStatusFilterChange = (e) => {
    setProjectStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (project_name) => {
    // fetchData('', project_name == 'all' ? '' : project_name)
    //    const parsedId = projectId === 'all' ? 'all' : parseInt(projectId, 10);
    // setFilterType(parsedId);
    //  if (parsedId === 'all') {
    //   setExpandedProjects({});
    // } else {
    //   setExpandedProjects({ [parsedId]: true });
    // }
  };

  const handlePriorityFilterChange = (priority) => {
    setPriorityFilter(priority);
  };
  const getFilteredAndSortedProjects = () => {
    return [...projects];

  //     if (projectStatusFilter !== 'all') {
  //   filtered = filtered.filter(project => project.status === projectStatusFilter);
  // }

  //   if (searchQuery) {
  //     const query = searchQuery.toLowerCase();
  //     filtered = filtered.filter(project => {
  //       const projectName = project.name || '';
  //       const projectDescription = project.description || '';


  //       const projectMatches =
  //         projectName.toLowerCase().includes(query) ||
  //         projectDescription.toLowerCase().includes(query);


  //       const suiteMatches = (projectTestSuites[project.project_id] || []).some(suite => {
  //         const suiteName = suite.suite_name || '';
  //         return suiteName.toLowerCase().includes(query);
  //       });

  //       return projectMatches || suiteMatches;
  //     });
  //   }

  //   if (filterType !== 'all') {
  //     filtered = filtered.filter(project => project.id === filterType);
  //   }

  //   if (statusFilter && statusFilter !== 'all') {
  //     filtered = filtered.filter(project => project.status === statusFilter);
  //   }

  //   return filtered;
  };

  const handleEditTestSuite = (projectId, testSuiteId) => {
    const project = projects.find(p => p.project_id === projectId);
    if (project) {
      const testSuite = testSuitList.find(ts => ts.suite_id === testSuiteId);
      testSuite['project_name'] = project.project_name
      if (testSuite) {
        setSelectedTestSuite(testSuite);
        setIsEditModalOpen(true);
      }
    }
  };
  const handleLinkTestCases = (testSuite, mode = 'link') => {
  setSelectedTestSuite(testSuite);
  setLinkCasesMode(mode);
  setIsLinkCasesModalOpen(true);
};

const handleViewExecutionHistory = (testSuite, e) => {
  if (e) e.stopPropagation();
  setExecutionHistorySuite(testSuite);
  setIsExecutionHistoryModalOpen(true);
};

const handleLinkCasesSuccess = () => {
  if (selectedTestSuite && selectedTestSuite.project_id) {
    const page = getSuitePageForProject(selectedTestSuite.project_id);
    getTestSuitForProjects(selectedTestSuite.project_id, page);
  }
};

  // const handleSaveTestSuite = (updatedTestSuite) => {
  //   // const updatedProjects = projects.map(project => {
  //   //   if (project.id === updatedTestSuite.projectId) {
  //   //     if (selectedTestSuite.projectId !== updatedTestSuite.projectId) {
  //   //       const oldProject = projects.find(p => p.id === selectedTestSuite.projectId);
  //   //       if (oldProject) {
  //   //         const updatedOldProject = {
  //   //           ...oldProject,
  //   //           testSuites: oldProject.testSuites.filter(ts => ts.id !== updatedTestSuite.id)
  //   //         };

  //   //         return {
  //   //           ...project,
  //   //           testSuites: [...testSuitList, updatedTestSuite]
  //   //         };
  //   //       }
  //   //     }

  //   //     return {
  //   //       ...project,
  //   //       testSuites: testSuitList.map(ts =>
  //   //         ts.id === updatedTestSuite.id ? updatedTestSuite : ts
  //   //       )
  //   //     };
  //   //   } else if (project.id === selectedTestSuite.projectId && selectedTestSuite.projectId !== updatedTestSuite.projectId) {
  //   //     return {
  //   //       ...project,
  //   //       testSuites: testSuitList.filter(ts => ts.id !== updatedTestSuite.id)
  //   //     };
  //   //   }
  //   //   return project;
  //   // });

  //   // setProjects(updatedProjects);
  //   // setOriginalProjects(updatedProjects);

  //   showActionFeedback(`Test suite "${updatedTestSuite.name}" updated successfully`, 'success');
  // };

  const handleCreateTestSuite = (newTestSuite) => {
    const updatedProjects = projects.map(project => {
      if (project.id === newTestSuite.projectId) {
        return {
          ...project,
          testSuites: [...testSuitList, newTestSuite]
        };
      }
      return project;
    });

    setProjects(updatedProjects);
    setOriginalProjects(updatedProjects);

    showActionFeedback(`Test suite "${newTestSuite.name}" created successfully`, 'success');
  };

  const handleDeleteTestSuite = async () => {
    if (!testSuiteToDelete || isDeletingTestSuite) return;

    const { projectId, suite } = testSuiteToDelete;
    setIsDeletingTestSuite(true);

    try {
      await axiosInstance.delete(`/test_suite/${suite.suite_id}`)
      showActionFeedback(`Test suite deleted successfully`, 'success');
      const page = getSuitePageForProject(projectId);
      await getTestSuitForProjects(projectId, page);
      setTestSuiteToDelete(null);
    } catch (err) {
      console.log(err)
    } finally {
      setIsDeletingTestSuite(false);
    }
    // showActionFeedback(`Deleting test suite #${testSuiteId}`, 'info');
  };

  // if (loading) {
  //   return (
  //     <div className="loading-container">
  //       <div className="loading-spinner"></div>
  //       <p>Loading test suites overview...</p>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button
          className="navbar-button primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  const filteredProjects = getFilteredAndSortedProjects();

  return (
    <div className="test-suites-overview-container">
      {actionFeedback && (
        <div className={`action-feedback ${actionFeedback.type}`}>
          <span>{actionFeedback.message}</span>
          <button onClick={() => setActionFeedback(null)}>×</button>
        </div>
      )}

      <div className="test-suites-header">
        <div>
          <h1 className="test-suites-title">Test Suites Overview</h1>
          <p className="test-suites-subtitle">Manage your test suites across all projects</p>
        </div>
        <div className="test-suites-actions">
          <button
            className="navbar-button primary"
            onClick={() => {
              if (!contextProject?.id) {
                showActionFeedback('Please select a project first', 'error');
                return;
              }
              createTestSuit(true, {
                project_id: contextProject.id,
                project_name: contextProject.name,
                name: contextProject.name,
              });
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Create Test Suite
          </button>
        </div>
      </div>

      <div className="stats-summary">
        <div className="stat-item">
          <div className="stat-icon home-icon"></div>
          <div className="stat-label">Projects</div>
          <div className="stat-value">{cardValues?.total_project_count}</div>
        </div>

        <div className="stat-item">
          <div className="stat-icon file-icon"></div>
          <div className="stat-label">Test Suites</div>
          <div className="stat-value">{cardValues.total_suites_all_projects}</div>
        </div>

        <div className="stat-item">
          <div className="stat-icon check-icon"></div>
          <div className="stat-label">Test Cases</div>
          <div className="stat-value">{cardValues.total_test_cases_all_projects}</div>
        </div>

        <div className="stat-item">
          <div className="stat-icon rate-icon"></div>
          <div className="stat-label">Avg. Pass Rate</div>
          <div className="stat-value">{cardValues.avg_pass_rate_all_projects}</div>
        </div>
      </div>
      {/* <div clas
      Name="filters-row"> */}
        {/* <div className="search-box">
          <input
            type="text"
            placeholder="Search projects, test suites..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
            disabled={searchInputDisabled}
          />
        </div> */}

        {/* <div className="filter-dropdown">
          <label>Project:</label>
          <select
            value={filterType}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Projects</option>
            {projectListForFilter.map(project => (
              <option key={project.project_id} value={project.project_name}>
                {project.project_name || project.name}
              </option>
            ))}
          </select>
        </div> */}

        {/* <div className="filter-dropdown" >
          <label style={{ marginTop: "8px" }}>Status:</label>
          <select
            value={projectStatusFilter}
            onChange={handleProjectStatusFilterChange}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div> */}

        {/* <div className="filter-dropdown">
          <label>Priority:</label>
          <select
            value={priorityFilter}
            onChange={(e) => handlePriorityFilterChange(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div> */}
      {/* </div> */}

      <div className="projects-list">
        {projects.length > 0 ? (
          projects.map(project => (

            <div key={project.project_id} className="project-card">
              <div className="project-header">
                <div className="project-header-main">
                  <div className="project-title-section">
                    <h2
                      className="project-name"
                      onClick={() => handleViewProjectTestSuites(project.id)}
                    >
                      {project.name || project.project_name}
                    </h2>
                    <ProjectTypeBadge type={project.project_type} />
                    <ProjectStatusBadge status={project.status} />
                  </div>

                  <div className="project-header-stats">
                    <div className="header-stat">
                      <div className="header-stat-label">TEST CASES</div>
                      <div className="header-stat-value">{project.total_test_cases}</div>
                    </div>

                    <div className="header-stat">
                      <div className="header-stat-label">PASSED</div>
                      <div className="header-stat-value passed">
                        {project.total_passed}
                      </div>
                    </div>

                    <div className="header-stat">
                      <div className="header-stat-label">FAILED</div>
                      <div className="header-stat-value failed">
                        {project.total_failed}
                      </div>
                    </div>

                    <div className="header-stat">
                      <div className="header-stat-label">PENDING</div>
                      <div className="header-stat-value pending"> {project.total_pending}</div>
                    </div>
                  </div>

                  {/* {filterType === 'all' && ( */}
                  <button
                    className={`expand-toggle ${expandedProjects[project.project_id] ? 'expanded' : ''}`}
                    onClick={(e) => toggleProjectExpansion(project.project_id, e)}
                    aria-label={expandedProjects[project.project_id] ? "Collapse project" : "Expand project"}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {/* )} */}
                </div>
              </div>

              {(expandedProjects[project.project_id]) && (
                <div className="project-content expanded">
                  <div className="test-suites-table-container">
                    <div className="table-scroll-area">
                    {tableLoading || projectSuitesLoading[project.project_id] ? (
                      <div className="table-loader">
                        <div className="loading-spinner"></div>
                      </div>
                    ) : (
                      <table className="test-suites-table">
                        <thead>
                          <tr>
                            <th>Test Suite</th>
                            <th>Test Cases</th>
                            <th>Pass Rate</th>
                            <th>Last Updated</th>
                            <th className="column-execution-history">Execution History</th>
                            <th className="column-actions">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectTestSuites[project.project_id]?.map(testSuite => (
                            <tr key={`${project.project_id}-${testSuite.suite_id}`}>
                              <td className="test-suite-name-cell" onClick={() => handleViewProjectTestSuites(project.project_id, testSuite.suite_id)}>
                                {testSuite.suite_name}
                              </td>
                              <td>{testSuite.test_case_count || 0}</td>
                              <td>
                                <span className={`pass-rate-badge ${(testSuite.passRate || 0) < 70 ? 'low' :
                                    (testSuite.average_pass_rate || 0) < 90 ? 'medium' : 'high'
                                  }`}>
                                  {testSuite.average_pass_rate || 0}%
                                </span>
                              </td>
                              <td>{formatDate(testSuite.updated_at)}</td>
                              <td className="column-execution-history">
                                <button
                                  className="action-btn execution-history"
                                  title="View Execution History"
                                  onClick={(e) => handleViewExecutionHistory(testSuite, e)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                  </svg>
                                </button>
                              </td>
                              <td className="column-actions">
                                <div className="test-suite-action-buttons">
                                  <button
                                    className="action-btn edit"
                                    title="Edit Test Suite"
                                    onClick={() => handleEditTestSuite(project.project_id, testSuite.suite_id)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </button>
                                  <button
                                    className="action-btn delete"
                                    title={testSuite.executed
                                      ? 'This test suite cannot be deleted because it has already been executed'
                                      : 'Delete Test Suite'}
                                    disabled={testSuite.executed}
                                    onClick={() => setTestSuiteToDelete({
                                      projectId: project.project_id,
                                      suite: testSuite,
                                    })}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </button>
                                  <button
                                    className="action-btn link"
                                    title="Link Test Cases"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLinkTestCases(testSuite, 'link');
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                    </svg>
                                  </button>
                                  <button
                                    className="action-btn unlink"
                                    title="Unlink Test Cases"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLinkTestCases(testSuite, 'unlink');
                                    }}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M17 7a5 5 0 0 1 0 7l-1 1"></path>
                                      <path d="M7 17a5 5 0 0 1 0-7l1-1"></path>
                                      <line x1="14" y1="10" x2="10" y2="14"></line>
                                      <line x1="1" y1="1" x2="23" y2="23"></line>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    </div>

                    <Pagination
                      page={getSuitePageForProject(project.project_id)}
                      totalPages={projectTestSuiteTotalPages[project.project_id] || 1}
                      onPageChange={(page) =>
                        handleTestSuitePageChange(project.project_id, page)
                      }
                      showPageSize={false}
                      showRange={false}
                    />
                  </div>
                </div>
              )}
            </div>

          ))
        ) : (
          <div className="no-results">
            <p>No projects found matching your criteria.</p>
          </div>
        )}

      </div>
      <Pagination
        page={cardValues.page}
        totalPages={cardValues.total_pages}
        onPageChange={(page) => {
          setCurrentPage(page);
        }}
        showPageSize={false}
        showRange={false}
      />

      <CreateTestSuiteModal
        isOpen={isCreateModalOpen}
        onClose={(skipFetch = false) => {
          if (skipFetch) {
            setIsCreateModalOpen(false);
            return;
          }
          closeCreationModal(testSuitForSelectedProj);
        }}
        onCreateTestSuite={handleCreateTestSuite}
        projects={testSuitForSelectedProj}
        defaultProjectId={contextProject?.id || testSuitForSelectedProj?.project_id || null}
      />
<LinkTestCasesModal
  isOpen={isLinkCasesModalOpen}
  onClose={() => setIsLinkCasesModalOpen(false)}
  testSuite={selectedTestSuite}
  projectId={selectedTestSuite?.project_id}
  onSuccess={handleLinkCasesSuccess}
  mode={linkCasesMode}
/>
      <ExecutionHistoryModal
        isOpen={isExecutionHistoryModalOpen}
        onClose={() => {
          setIsExecutionHistoryModalOpen(false);
          setExecutionHistorySuite(null);
        }}
        entityType="suite"
        entity={executionHistorySuite}
      />
      {testSuiteToDelete && (
        <div data-rt-modal-overlay className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => {
            if (!isDeletingTestSuite) {
              setTestSuiteToDelete(null);
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-[fadeIn_0.2s_ease-out]"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                Delete Test Suite
              </h2>
              <button
                onClick={() => {
                  if (!isDeletingTestSuite) {
                    setTestSuiteToDelete(null);
                  }
                }}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                ✕
              </button>
            </div>

            <div className="px-6 py-5 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 6h18M8 6V4h8v2m-9 0v14a2 2 0 002 2h6a2 2 0 002-2V6M10 11v6M14 11v6"
                  />
                </svg>
              </div>

              <p className="text-gray-700 text-sm">
                Are you sure you want to delete this test suite?
              </p>

              <p className="mt-2 font-semibold text-gray-900 text-base break-words">
                {testSuiteToDelete?.suite?.suite_name}
              </p>

              <p className="mt-2 text-xs text-gray-500">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => setTestSuiteToDelete(null)}
                disabled={isDeletingTestSuite}
                className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              <button
                onClick={handleDeleteTestSuite}
                disabled={isDeletingTestSuite}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingTestSuite ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      <EditTestSuiteModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTestSuite(null);
        }}
        onSaveSuccess={() => {
          if (selectedTestSuite?.project_id) {
            closeEidtTestForm(selectedTestSuite);
          }
          setIsEditModalOpen(false);
          setSelectedTestSuite(null);
        }}
        // onSaveTestSuite={handleSaveTestSuite}
        projects={projects}
        testSuite={selectedTestSuite}
      />
    </div>
  );
};


export default TestSuitesOverview